import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { parseISO, format } from 'date-fns';
import { Link } from "react-router-dom";
import { useSession } from "../components/SessionProvider";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import DataDialog from "../components/ui/DataDialog";

import Deadline from "../components/Deadline";
import { useApi } from "../hooks/useApi";
import { useDownloadWithToken } from "../hooks/downloadApi";

const SiteDetails = () => {
    const { siteId } = useParams();  // Get the site ID from the URL
    const [siteData, setSiteData] = useState(null);
    const [siteInvoiceData, setSiteInvoiceData] = useState(null); // State to hold site invoice data
    const { apiCall } = useApi();
    const { downloadWithToken } = useDownloadWithToken();

    const invoicesUrl = import.meta.env.VITE_INVOICES_URL;
    const combinedInvoicesUrl = import.meta.env.VITE_COMBINED_INVOICES_URL;
    const initialized = useRef(false);
    const { userLogin } = useSession();
    const [inProgress, setInProgress] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCodingDate, setSelectedCodingDate] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [dataDialogData, setDataDialogData] = useState('');
    const [regenerationInProgress, setRegenerationInProgress] = useState(false);
    const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
    const [regenerationDate, setRegenerationDate] = useState(null);
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            // Fetch site details from API using the ID
            apiCall(`fetch_site`, { params: { site_id: siteId } })
                .then(response => response.json())
                .then(data => setSiteData(data))
                .then(() => {
                    apiCall(`fetch_site_invoice_data`, { params: { site_id: siteId } })
                        .then(response => response.json())
                        .then(data => setSiteInvoiceData(data))
                        .catch(error => console.error("Error fetching site invoice data:", error));
                })
                .catch(error => console.error("Error fetching site data:", error));


        }
    }, [siteId]);
    const downloadInvoiceWithoutWatermark = async (selectedCodingDate, file_name) => {
        if (!selectedCodingDate) {
            alert('no coding date');
            return;
        }

        if (inProgress) return;
        setInProgress(true);

        setPrintLoading(true);
        try {
            await downloadWithToken('generateSummaryInvoiceWithoutStamp', {
                invoice_date: selectedCodingDate,
                site_id: siteId,
                download_mode: true
            }, `no_stamp_${file_name}`);
        } catch (error) {
            console.error('Download failed:', error);
            // Optionally show error message to user
        } finally {
            setPrintLoading(false);
            setInProgress(false);
        }
    };
    const deleteInvoice = async () => {

        if (!selectedCodingDate) {
            alert('no coding date');
        }

        if (inProgress) return;
        setInProgress(true);

        try {
            const response = await apiCall(`delete_summary_invoice`, { params: { siteId: siteId, coding_date: selectedCodingDate } });
            const data = await response.json();
            setInProgress(false);
            if (!data.success) {
                throw new Error(data.error);
            }
            window.location.reload();
        } catch (error) {
            alert(error);
        }
    };

    const showGate3Data = async (po, sitenumber) => {

        try {
            const response = await apiCall(`fetch_gate3_api_output`, { params: { project_number: sitenumber, po_number: po } });
            const data = await response.json();
            setDataDialogData(data);
            setDataDialogOpen(true);

        } catch (error) {
            console.log(error);
        }
    };

    const regenerateCodingSheet = async () => {
        if (regenerationInProgress || !regenerationDate) {
            return;
        }
        setRegenerationInProgress(true);
        try {
            const data = await apiCall(`generateSummaryInvoice`, { method: "POST", params: { site_id: siteId, invoice_date: regenerationDate, u: userLogin.user_email } }).then(res => res.json());
            data.error ? alert(data.error ?? "Error regenerating coding sheet.") : '';
        } catch (err) { alert("Error submitting coding sheet."); console.error(err); } finally { setShowRegenerateDialog(false); setRegenerationInProgress(false); }

    };
    return (

        <div className="p-6">
            {dataDialogOpen && (
                <DataDialog
                    open={dataDialogOpen}
                    message={dataDialogData}
                    onCancel={() => { setDataDialogOpen(false) }}
                />
            )}
            {dialogOpen && (
                <ConfirmDialog
                    open={dialogOpen}
                    title="Delete Invoice"
                    message={
                        <>
                            Are you sure you want to remove this invoice along with its data?
                            This action cannot be undone.
                        </>
                    }
                    inProgress={inProgress}
                    confirmText="Confirm Delete"
                    onConfirm={deleteInvoice}
                    onCancel={() => { setDialogOpen(false) }}
                />
            )}

            <ConfirmDialog
                open={showRegenerateDialog}
                title="Regenerate Summary Invoice"
                message={
                    <>
                        Are you sure you want to regenerate the Summary Invoice?<br />
                        Do this if the coding sheet looks correct but the Summary Invoice is incorrect.
                    </>
                }
                inProgress={regenerationInProgress}
                confirmText="Regenerate"
                onConfirm={regenerateCodingSheet}
                onCancel={() => { setShowRegenerateDialog(false) }}
            />

            {siteData ? <div className="flex justify-between uppercase mb-4">
                <h1 className="text-2xl font-bold">{siteData.site_number} - {siteData.site_name}</h1>
                <Link to={`/`} className="normal-case btn btn-primary self-end">
                    Back
                </Link>
            </div> : <p>Loading...</p>}

            <Deadline />


            {/* <p className="mb-3">*Please email <a target="_blank" href="mailto:oxfordhq@oxfordbuilders.ca">oxfordhq@oxfordbuilders.ca</a> with the job site # that you require access to if needed*
            </p> */}
            {siteInvoiceData && (
                <table className="min-w-full border-collapse border border-gray-300">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="border p-2 text-left">Summary Invoice</th>
                            <th className="border p-2 text-left">
                                Summary Invoice Backup
                                <br /><span className="text-sm">(If Needed)</span>
                            </th>
                            <th className="border p-2 text-left">Cycle Billing Date</th>
                            <th className="border p-2 text-left">Coding Sheet</th>
                            <th className="border p-2 text-left">Submitted By</th>

                        </tr>
                    </thead>
                    <tbody>
                        {siteInvoiceData.map((row, index) => {
                            return (
                                <tr key={index} className="border">
                                    <td className="border p-2">{row.summary_invoice && (
                                        <div className="flex justify-between items-center">
                                            <a href={invoicesUrl + row.summary_invoice} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{row.summary_invoice}</a>
                                            {userLogin.isAdminManager && (
                                                <>
                                                    <button
                                                        className="underline"
                                                        onClick={() => { downloadInvoiceWithoutWatermark(row.coding_date, row.summary_invoice); }}
                                                        disabled={printLoading}
                                                    >
                                                        {printLoading ? 'Downloading...' : 'Download (no stamp)'}
                                                    </button>
                                                    <button onClick={() => { setRegenerationDate(row.coding_date); setShowRegenerateDialog(true); }}
                                                        disabled={regenerationInProgress} className="inline-flex items-center justify-center p-2.5 text-gray-600 transition-colors duration-200 rounded-full bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Redo">
                                                        {regenerationInProgress && regenerationDate === row.coding_date ? 'wait...' : 'Regenerate Invoice'}
                                                    </button>
                                                </>
                                            )}

                                        </div>
                                    )}</td>
                                    <td className="border p-2">
                                        {row.combined_invoice && (
                                            <a href={combinedInvoicesUrl + row.combined_invoice} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mr-2">{row.combined_invoice}</a>

                                        )}
                                    </td>
                                    <td className="border p-2">{format(parseISO(row.coding_date), 'dd-MMM-yyyy')}</td>

                                    <td className="border p-2">
                                        {
                                            <div className="flex justify-between items-center">

                                                <Link to={`/coding-sheet/${row.site_id}/${row.coding_date}`} className="text-blue-500 underline">
                                                    View Coding Sheet
                                                </Link>
                                                {userLogin.isAdminManager && row.gate_3_po && (

                                                    <button onClick={(e) => { showGate3Data(row.gate_3_po, siteData.site_number) }} className="ml-3 underline cursor-pointer">Gate3 API</button>

                                                )}
                                                {userLogin.isAdminManager && (

                                                    <button onClick={(e) => { setSelectedCodingDate(row.coding_date); setDialogOpen(true); }} className="ml-3 inline-flex items-center justify-center p-2.5 text-gray-600 transition-colors duration-200 rounded-full bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-[44px] h-[44px]">X</button>

                                                )}
                                            </div>
                                        }
                                    </td>
                                    <td className="border p-2">
                                        {row.submitter_email}
                                    </td>

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default SiteDetails;
